import React, { useEffect, useState } from 'react';
import { Table, Switch, Form, Button, Select, Spin, Alert, Tag, Badge } from 'antd';
import {
  ApiOutlined, BellOutlined, CheckCircleOutlined, ExclamationCircleOutlined, NotificationOutlined, TeamOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import TelegramLinkModal from '../components/TelegramLinkModal';
import ConfirmationNoticeRecipientsCard from '../modules/notifications/components/ConfirmationNoticeRecipientsCard';
import {
  canManageGlobalNotificationSettings,
  canManageNotificationRecipientPool,
} from '../modules/notifications/notificationSettingsAccessPolicy';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import { ObjectCardFacts, ObjectCardSection } from '../components/layout/ObjectCard';
import '../components/layout/ObjectCardExtras.css';
import './NotificationSettings.css';

interface NotificationSetting {
  id: string;
  eventType: string;
  enabled: boolean;
  description: string;
}

interface ServiceStatus {
  email: { configured: boolean };
  telegram: { configured: boolean; botUsername: string | null };
}

interface RecipientUser {
  id: string;
  name: string;
  email: string;
  role: string;
  isNotificationRecipient: boolean;
}

interface UserPreferences {
  id?: string;
  userId: string;
  emailEnabled: boolean;
  telegramEnabled: boolean;
  telegramChatId: string | null;
  telegramUsername: string | null;
  language: string;
  isNotificationRecipient?: boolean;
}

const NotificationSettings: React.FC = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const feedback = useFeedback();
  const currentUser = authService.getCurrentUser();
  const canManageGlobalSettings = canManageGlobalNotificationSettings(currentUser);
  const canManageRecipientPool = canManageNotificationRecipientPool(currentUser);
  const canManageAdminSections = canManageGlobalSettings && canManageRecipientPool;
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<NotificationSetting[]>([]);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [recipients, setRecipients] = useState<RecipientUser[]>([]);
  const [updatingRecipientIds, setUpdatingRecipientIds] = useState<Set<string>>(new Set());
  // User preferences state
  const [prefsForm] = Form.useForm();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [telegramModalVisible, setTelegramModalVisible] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const promises: Promise<any>[] = [
        api.get<ServiceStatus>('/notifications/status'),
        api.get<UserPreferences>('/notifications/preferences'),
      ];
      if (canManageAdminSections) {
        promises.push(
          api.get<NotificationSetting[]>('/notifications/settings'),
          api.get<RecipientUser[]>('/notifications/recipients-config')
        );
      }
      const results = await Promise.all(promises);
      setServiceStatus(results[0].data);
      const prefs = results[1].data;
      setPreferences(prefs);
      prefsForm.setFieldsValue({
        emailEnabled: prefs.emailEnabled,
        telegramEnabled: prefs.telegramEnabled,
        language: prefs.language || 'pl',
      });
      if (canManageAdminSections) {
        setSettings(results[2].data);
        setRecipients(results[3].data);
      }
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    const eventTypeLabel = t(`notifications:eventTypes.${settings.find(s => s.id === id)?.eventType}`);
    setUpdatingIds(prev => new Set(prev).add(id));
    try {
      await api.put(`/notifications/settings/${id}`, { enabled });
      setSettings(prev =>
        prev.map(s => (s.id === id ? { ...s, enabled } : s))
      );
      feedback.success(t('notifications:settings_page.saved', { eventType: eventTypeLabel }));
    } catch (error) {
      feedback.error(
        getApiErrorMessage(error, t, 'notifications:errors.toggleSettingFailed', undefined, { eventType: eventTypeLabel })
      );
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleRecipientToggle = async (userId: string, isNotificationRecipient: boolean) => {
    const recipientName = recipients.find(r => r.id === userId)?.name;
    setUpdatingRecipientIds(prev => new Set(prev).add(userId));
    try {
      await api.put(`/notifications/recipients-config/${userId}`, { isNotificationRecipient });
      setRecipients(prev =>
        prev.map(r => (r.id === userId ? { ...r, isNotificationRecipient } : r))
      );
      feedback.success(t('notifications:recipients.saved', { name: recipientName }));
    } catch (error) {
      feedback.error(
        getApiErrorMessage(error, t, 'notifications:recipients.saveFailed', undefined, { name: recipientName })
      );
    } finally {
      setUpdatingRecipientIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleSavePrefs = async (values: { emailEnabled: boolean; telegramEnabled: boolean; language: string }) => {
    setSavingPrefs(true);
    try {
      await api.put('/notifications/preferences', values);
      feedback.success(t('notifications:preferences_page.saved'));
      fetchData();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:errors.savePrefsFailed'));
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    try {
      await api.delete('/notifications/telegram/unlink');
      feedback.success(t('notifications:telegram.unlinkSuccess'));
      fetchData();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:telegram.unlinkFailed'));
    }
  };

  const columns = [
    {
      title: t('notifications:settings_page.eventType'),
      dataIndex: 'eventType',
      key: 'eventType',
      width: '34%',
      ellipsis: true,
      render: (eventType: string) => (
        <span className="obj-strong">{t(`notifications:eventTypes.${eventType}`)}</span>
      ),
    },
    {
      title: t('notifications:settings_page.description_col'),
      dataIndex: 'eventType',
      key: 'description',
      onCell: () => ({ className: 'notification-settings__description' }),
      render: (eventType: string) => t(`notifications:eventDescriptions.${eventType}`),
    },
    {
      title: t('notifications:settings_page.enabled'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean, record: NotificationSetting) => (
        <Switch
          checked={enabled}
          loading={updatingIds.has(record.id)}
          onChange={(checked) => handleToggle(record.id, checked)}
        />
      ),
    },
  ];

  const recipientColumns = [
    {
      title: t('notifications:recipients.name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name: string) => <span className="obj-strong">{name}</span>,
    },
    {
      title: t('notifications:recipients.email'),
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
    },
    {
      title: t('notifications:recipients.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'ADMIN' ? 'red' : role === 'OPERATIONS' ? 'blue' : 'default'}>
          {t(`common:userRole.${role}`, { defaultValue: role })}
        </Tag>
      ),
    },
    {
      title: t('notifications:recipients.available'),
      dataIndex: 'isNotificationRecipient',
      key: 'isNotificationRecipient',
      width: 120,
      render: (isRecipient: boolean, record: RecipientUser) => (
        <Switch
          checked={isRecipient}
          loading={updatingRecipientIds.has(record.id)}
          onChange={(checked) => handleRecipientToggle(record.id, checked)}
        />
      ),
    },
  ];

  if (loading) {
    return <div className="obj-loading"><Spin size="large" /></div>;
  }

  const isTelegramLinked = !!preferences?.telegramChatId;
  const isTelegramConfigured = serviceStatus?.telegram.configured;
  const isRecipient = preferences?.isNotificationRecipient ?? false;
  const showServiceStatus = Boolean(serviceStatus && canManageGlobalSettings);

  const statusValue = (ready: boolean, detail?: string | null) => (
    <Badge
      status={ready ? 'success' : 'default'}
      text={ready
        ? `${t('notifications:service_status.ready')}${detail ? ` · @${detail}` : ''}`
        : t('notifications:service_status.notReady')}
    />
  );

  // Apple-style settings (DESIGN.md „Direction since 2026-10-19”): grouped
  // lists with the label left and the switch or field right; tables span the row.
  return (
    <div className="obj-card notification-settings-page">
      <div className="obj-card__body">
        {/* My preferences - visible to all users */}
        <ObjectCardSection
          title={t('notifications:preferences_page.title')}
          icon={<BellOutlined />}
          tone="pink"
          hint={t('notifications:preferences_page.description')}
          wide={!showServiceStatus}
          actions={isRecipient ? (
            <Button type="primary" size="small" loading={savingPrefs} onClick={() => prefsForm.submit()}>
              {t('notifications:preferences_page.save')}
            </Button>
          ) : undefined}
        >
          {isRecipient ? (
            <Form
              form={prefsForm}
              layout="vertical"
              onFinish={handleSavePrefs}
              onFinishFailed={() => feedback.warning(t('notifications:fillRequired'))}
              scrollToFirstError
            >
              <div className="obj-form">
                <Form.Item
                  className="tool-form__end"
                  label={t('notifications:preferences_page.emailNotifications')}
                  tooltip={t('notifications:preferences_page.emailEnabled')}
                >
                  <Form.Item name="emailEnabled" valuePropName="checked" noStyle>
                    <Switch aria-label={t('notifications:preferences_page.emailNotifications')} />
                  </Form.Item>
                </Form.Item>

                <Form.Item
                  className="tool-form__end"
                  label={t('notifications:preferences_page.telegramNotifications')}
                  tooltip={t('notifications:preferences_page.telegramEnabled')}
                >
                  {!isTelegramConfigured && (
                    <span className="tool-form__note" title={t('notifications:service_status.telegramNotConfigured')}>
                      {t('notifications:service_status.telegramNotConfigured')}
                    </span>
                  )}
                  {isTelegramConfigured && (isTelegramLinked ? (
                    <>
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        {t('notifications:preferences_page.telegramLinked')} (@{preferences?.telegramUsername})
                      </Tag>
                      <Button size="small" danger onClick={handleUnlinkTelegram}>
                        {t('notifications:preferences_page.unlinkTelegram')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Tag icon={<ExclamationCircleOutlined />} color="warning">
                        {t('notifications:preferences_page.telegramNotLinked')}
                      </Tag>
                      <Button size="small" onClick={() => setTelegramModalVisible(true)}>
                        {t('notifications:preferences_page.linkTelegram')}
                      </Button>
                    </>
                  ))}
                  <Form.Item name="telegramEnabled" valuePropName="checked" noStyle>
                    <Switch
                      aria-label={t('notifications:preferences_page.telegramNotifications')}
                      disabled={!isTelegramLinked || !isTelegramConfigured}
                    />
                  </Form.Item>
                </Form.Item>

                <Form.Item name="language" label={t('notifications:preferences_page.language')}>
                  <Select
                    aria-label={t('notifications:preferences_page.language')}
                    options={[
                      { value: 'pl', label: 'Polski' },
                      { value: 'en', label: 'English' },
                    ]}
                  />
                </Form.Item>
              </div>
              <button type="submit" hidden tabIndex={-1} />
            </Form>
          ) : (
            <Alert
              message={t('notifications:preferences_page.notAuthorized')}
              type="warning"
              showIcon
            />
          )}
        </ObjectCardSection>

        {/* Service status - admin only */}
        {showServiceStatus && serviceStatus && (
          <ObjectCardSection title={t('notifications:service_status.title')} icon={<ApiOutlined />} tone="gray">
            <ObjectCardFacts
              items={[
                { label: t('notifications:service_status.email'), value: statusValue(serviceStatus.email.configured) },
                {
                  label: t('notifications:service_status.telegram'),
                  value: statusValue(serviceStatus.telegram.configured, serviceStatus.telegram.botUsername),
                },
              ]}
            />
          </ObjectCardSection>
        )}

        {/* Admin-only sections */}
        {canManageAdminSections && (
          <>
            <ObjectCardSection
              wide
              title={t('notifications:settings_page.title')}
              icon={<NotificationOutlined />}
              tone="orange"
              count={settings.length}
              hint={t('notifications:settings_page.description')}
            >
              <Table
                dataSource={settings}
                columns={columns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </ObjectCardSection>

            <ConfirmationNoticeRecipientsCard />

            <ObjectCardSection
              wide
              title={t('notifications:recipients.title')}
              icon={<TeamOutlined />}
              tone="indigo"
              count={recipients.length}
              hint={t('notifications:recipients.description')}
            >
              <Table
                dataSource={recipients}
                columns={recipientColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </ObjectCardSection>
          </>
        )}
      </div>

      <TelegramLinkModal
        visible={telegramModalVisible}
        onClose={() => setTelegramModalVisible(false)}
        onLinked={() => { setTelegramModalVisible(false); fetchData(); }}
      />
    </div>
  );
};

export default NotificationSettings;
